import kebabCase from 'lodash/kebabCase';
import {
  acqStatistics,
  FundUmbrella,
  PortfolioCharsDTO,
  ProductDTO,
  ProductType,
  Statistic,
  StatisticId,
  StatisticsAndPositionsData,
  StatisticSuppressionSet,
  STATISTIC_ID_BENCHMARK_SUFFIX,
  PortfolioCharsHistoryDTO,
  PortfolioCharsHistory,
} from '@types';
import { MapperFactory } from '../mapper-factory';
import { Mapper, MapperParams } from '../type.mapper';
import { TranslateService } from '@shared/translate/translate.service';
import { Logger } from '@utils/logger';
import { ProfileService } from '@services';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const logger = Logger.getLogger('StatisticMapper');

const createBaseFimesKey = (rawStat: PortfolioCharsDTO): string => {
  if (acqStatistics.includes(rawStat.elemname as StatisticId)) {
    // ACQ stats use With/WithoutCash and With/WithoutQuality instead
    let base = '';
    if (rawStat.hascurrency === 'Y') {
      base += 'WithCash';
    }
    if (rawStat.hascurrency === 'N') {
      base += 'WithoutCash';
    }
    if (rawStat.hasquality === 'Y') {
      base += 'WithQuality';
    }
    if (rawStat.hasquality === 'N') {
      base += 'WithoutQuality';
    }
    return base;
  }

  // other stats just have With/WithoutDerivatives
  if (rawStat.hasderivatives === 'Y') {
    return 'WithDerivatives';
  }
  if (rawStat.hasderivatives === 'N') {
    return 'WithoutDerivatives';
  }
  // return empty string if hasderivatives is null/undefined/empty string etc
  return '';
};

const createTooltip = (
  translateService: TranslateService,
  labelKey: string,
  fimesLabelKey: string,
  isDebug?: boolean
): string => {
  if (isDebug) {
    return `[${labelKey}-tooltip]<br />[${fimesLabelKey}-tooltip]`;
  }

  const primaryTooltip: string = translateService.tooltip(labelKey);
  logger.debug('primaryTooltip:', labelKey, primaryTooltip);
  const secondaryTooltip: string = translateService.tooltip(fimesLabelKey);
  logger.debug('secondaryTooltip:', fimesLabelKey, secondaryTooltip);

  let tooltip = '';
  if (primaryTooltip) {
    tooltip += `<p>${primaryTooltip}</p>`;
  }
  if (secondaryTooltip) {
    tooltip += `<p>${secondaryTooltip}</p>`;
  }
  return tooltip;
};

/**
 * This will return original stats array if no data or stat is suppressed
 * Stat data will be appended to the stats array if the data exists and isn't suppressed
 * Benchmark stat data will also be appended if it exists and isn't suppressed
 * In debug mode, stats and data with debug values will be appended regardless of suppression
 */
const createStatReducer = (mapperParams: MapperParams, isLoggedIn: boolean) => (
  stats: Statistic[],
  rawStat: PortfolioCharsDTO
): Statistic[] => {
  const statisticId: StatisticId = rawStat.elemname as StatisticId;
  const labelKey = `products.statistic-${statisticId}`;
  const baseStat: Statistic = {
    id: statisticId,
    caveatPlacement: `Statistics:PortfolioStatistic${statisticId}`,
    val: rawStat.elemvaluelocal,
    asOfDate: rawStat.asofdate,
    // use translated label. Fallback to BR label if not present
    label:
      rawStat?.elemtransname ||
      mapperParams.translateService.instant(labelKey, mapperParams.fundId),
    benchmarkId: rawStat.bmno,
    benchmarkName: rawStat.bmname,
  };

  if (baseStat.asOfDate) {
    baseStat.asOfLabel = mapperParams.translateService.instant(
      'products.as-of'
    );
  }
  if (rawStat.frequency) {
    baseStat.updateFrequency = mapperParams.translateService.instant(
      `products.updated-${rawStat.frequency}`
    );
  }
  // set FIMES properties
  const baseFimesKey: string = createBaseFimesKey(rawStat);
  baseStat.secondaryCaveatPlacement = `${baseStat.caveatPlacement}${baseFimesKey}`;
  baseStat.tooltip = createTooltip(
    mapperParams.translateService,
    labelKey,
    `${labelKey}-${kebabCase(baseFimesKey)}`,
    mapperParams.isShowLabelKeys
  );

  // 1. check if statistic is suppressed for whole site
  const isSiteSuppressed: boolean = mapperParams.config.isPortfolioStatisticSuppressedBySite(
    statisticId
  );
  if (isSiteSuppressed) {
    if (mapperParams.isDebugMode) {
      // add a debug stat
      stats.push({
        ...baseStat,
        val: `DEBUG: Statistic suppressed for whole site: ${baseStat.val}`,
      });
    }
    //  stat suppressed, so return stats un-altered
    return stats;
  }

  // 2. check if statistic is suppressed by particular set
  const suppressionSet: StatisticSuppressionSet = mapperParams.config.getPortfolioStatisticSuppressionSet(
    statisticId,
    mapperParams.fundId,
    isLoggedIn
  );
  if (suppressionSet) {
    if (mapperParams.isDebugMode) {
      // add a debug stat
      stats.push({
        ...baseStat,
        val: `DEBUG: Statistic suppressed by set '${suppressionSet.id}': ${baseStat.val}`,
      });
    }
    //  stat suppressed, so return stats un-altered
    return stats;
  }

  // 3. data exists and not suppressed, so add it to results array
  if (baseStat.val) {
    // NGC-4414 catches empty string values
    stats.push(baseStat);
  } else if (mapperParams.isDebugMode) {
    // add a debug stat
    stats.push({
      ...baseStat,
      val: `DEBUG: Value is empty`,
    });
  }

  // 4. check if benchmark data
  if (rawStat.elemvaluelocalbm || mapperParams.isDebugMode) {
    const bmLabelKey = `${labelKey}-benchmark`;
    const benchmarkId: StatisticId = `${statisticId}${STATISTIC_ID_BENCHMARK_SUFFIX}` as StatisticId;
    let val: string = rawStat.elemvaluelocalbm;
    if (!val && mapperParams.isDebugMode) {
      val = `DEBUG: No benchmark data`;
    }
    const baseBenchmarkStat: Statistic = {
      ...baseStat,
      id: benchmarkId,
      mainStatisticId: statisticId,
      // NB: PDS don't have labels for benchmark stats yet, so use BR label
      label: mapperParams.translateService.instant(
        bmLabelKey,
        mapperParams.fundId
      ),
      val,
      caveatPlacement: `Statistics:PortfolioStatistic${benchmarkId}`,
    };
    baseBenchmarkStat.secondaryCaveatPlacement = `${baseBenchmarkStat.caveatPlacement}${baseFimesKey}`;
    baseBenchmarkStat.tooltip = createTooltip(
      mapperParams.translateService,
      bmLabelKey,
      `${bmLabelKey}-${kebabCase(baseFimesKey)}`,
      mapperParams.isShowLabelKeys
    );

    // 5. check if benchmark is suppressed for this fund
    if (
      !mapperParams.config.isPortfolioStatisticSuppressedBySite(benchmarkId)
    ) {
      const benchmarkSuppressionSet: StatisticSuppressionSet = mapperParams.config.getPortfolioStatisticSuppressionSet(
        benchmarkId,
        mapperParams.fundId,
        isLoggedIn
      );
      if (!benchmarkSuppressionSet) {
        // add benchmark stat
        stats.push(baseBenchmarkStat);
      } else if (mapperParams.isDebugMode) {
        // add a debug stat
        stats.push({
          ...baseBenchmarkStat,
          val: `DEBUG: Benchmark Statistic suppressed by set '${benchmarkSuppressionSet.id}': ${baseBenchmarkStat.val}`,
        });
      }
    }
  }

  return stats;
};

/**
 * example fund:
 * https://en-us.staging.ftsites.com/investments/options/mutual-funds/products/4402/A/franklin-biotechnology-discovery-fund/FBDIX#portfolio
 */
export class StatisticMapper extends Mapper<StatisticsAndPositionsData> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(
    productDto: ProductDTO,
    isLoggedIn: boolean
  ): StatisticsAndPositionsData {
    const rawData: PortfolioCharsDTO[] =
      productDto.portfolio.portfoliochars || [];

    const portfolioHistoricData: PortfolioCharsHistoryDTO[] =
      productDto.portfolio.portfoliocharshistory || [];

    // starts with an array of data stats, and reduces this to stats that aren't suppressed.
    // benchmark data (that isn't suppressed) gets added as additoinal stats
    // in debug mode, stats with no data or suppressed, will appear but have DEBUG values
    const stats: Statistic[] = rawData.reduce(
      createStatReducer(this.mapperParams, isLoggedIn),
      []
    );

    const potrfolioHistoricTableData: PortfolioCharsHistory[] = portfolioHistoricData.map(
      (data) => this.createMapper(PortfolioCharsHistoryMapper).toDomain(data)
    );

    return stats.length > 0
      ? {
          productType: productDto.producttype as ProductType,
          fundUmbrella: productDto.fundumbrlla as FundUmbrella,
          statistics: stats,
          portfolioCharsHistory: potrfolioHistoricTableData,
        }
      : null;
  }
}

/**
 * DCE-1155
 *  exapmle fund: https://en-us.staging.ftsites.com/investments/options/money-market-funds
 *  /products/29386/SINGLCLASS/franklin-on-chain-u-s-government-money-fund/FOBXX
 */
export class PortfolioCharsHistoryMapper extends Mapper<PortfolioCharsHistory> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(
    portfolioCharsHistoryDto: PortfolioCharsHistoryDTO
  ): PortfolioCharsHistory {
    const statisticId: StatisticId = portfolioCharsHistoryDto.elemname as StatisticId;
    const labelKey = `products.statistic-${statisticId}`;

    // TODO :- move caveat and tooltip creation in component as it required only once
    const caveatPlacement = `Statistics:PortfolioStatistic${statisticId}`;
    const tooltip = createTooltip(
      this.mapperParams.translateService,
      labelKey,
      `${labelKey}-}`,
      this.mapperParams.isShowLabelKeys
    );

    return {
      asOfDate: portfolioCharsHistoryDto.asofdate,
      asOfDateStd: portfolioCharsHistoryDto.asofdatestd,
      elementName: portfolioCharsHistoryDto.elemname,
      elementDisplayName: portfolioCharsHistoryDto.elemdispname,
      elememtTranslatedName: portfolioCharsHistoryDto.elemtransname,
      elementValueStd: portfolioCharsHistoryDto.elemvaluestd,
      elementValueLocal: portfolioCharsHistoryDto.elemvaluelocal,
      frequency: portfolioCharsHistoryDto.frequency,
      label:
        portfolioCharsHistoryDto?.elemtransname ||
        this.mapperParams.translateService.instant(
          labelKey,
          this.mapperParams.fundId
        ),
      tooltip,
      caveatPlacement,
    };
  }
}
